import { BookingAddOnPaymentDto, BookingAddOnPaymentDtoCannotPayReasonEnum } from '@hqo/resource-booking-client';
import { StatusPillVariant } from 'components/booking-tile/redesigned/status-pill.util';

/**
 * Where an add-on order stands, in the shape the pill beside the vendor name draws it.
 *
 * `variant` is the booking pill's own union rather than a tone of our own, so a restyle there moves
 * this pill with it.
 */
export interface AddOnStatus {
  labelId: string;
  variant: StatusPillVariant;
}

/*
 * Every entry is `cannotPayReason` read back, never re-derived: RBS' `add-ons.payment-flags.ts`
 * resolves the reason and inferring one here gets it wrong (an expired session moves to
 * `payment_failed`, it does not stay pending).
 *
 * `workOrderStatus` must not be used here. It is a status name, and a building can rename its
 * statuses and bulk-provision custom ones carrying free text, so printing it would put
 * untranslated customer-authored strings in front of a tenant. `isVendorUnderway` reduces it to
 * the one bit that is safe to state.
 */
const BY_CANNOT_PAY_REASON: Record<BookingAddOnPaymentDtoCannotPayReasonEnum, AddOnStatus | null> = {
  /*
   * RBS resolves cancelled ahead of everything else -- a cancelled order's work order can still
   * report `pending_payment` with a live session for the seconds before work-orders acts -- so the
   * enum arrives already ranked and nothing here re-ranks it.
   */
  ORDER_CANCELLED: { labelId: 'add_on_status_pill_cancelled', variant: 'negative' },
  CHARGE_FAILED: { labelId: 'add_on_status_pill_charge_failed', variant: 'negative' },
  QUOTE_REJECTED: { labelId: 'add_on_status_pill_quote_rejected', variant: 'negative' },
  QUOTE_OFFERED: { labelId: 'add_on_status_pill_quote_offered', variant: 'pending' },
  AWAITING_APPROVAL: { labelId: 'add_on_status_pill_awaiting_approval', variant: 'pending' },
  AWAITING_CHECKOUT: { labelId: 'add_on_status_pill_awaiting_checkout', variant: 'pending' },

  /*
   * `null` means "ask the vendor question first", not "no pill": the figures already state these
   * three, so a paid order the vendor has picked up should read "In progress" instead.
   * `WHEN_NOTHING_IS_UNDERWAY` below answers if nothing else did.
   */
  ALREADY_PAID: null,
  REFUNDED: null,
  AWAITING_PRICE: null,
};

/**
 * What the three deferring reasons settle to when the vendor has not started.
 *
 * `Partial` on purpose: the exhaustiveness guard belongs on the one map a new reason must be
 * thought about in. A reason that defers there and is missing here draws no pill, same as any
 * unmapped reason.
 */
const WHEN_NOTHING_IS_UNDERWAY: Partial<Record<BookingAddOnPaymentDtoCannotPayReasonEnum, AddOnStatus>> = {
  ALREADY_PAID: { labelId: 'add_on_status_pill_paid', variant: 'positive' },
  REFUNDED: { labelId: 'add_on_status_pill_refunded', variant: 'negative' },
  AWAITING_PRICE: { labelId: 'add_on_status_pill_awaiting_price', variant: 'pending' },
};

const VENDOR_STARTED: AddOnStatus = { labelId: 'add_on_status_pill_vendor_started', variant: 'pending' };

/**
 * Where this order stands, or null when the figures above have already said it.
 *
 * `BY_CANNOT_PAY_REASON` is a full `Record` rather than a `satisfies` subset so the next client
 * bump that adds a reason fails the build here instead of silently matching nothing.
 */
export const addOnStatus = (payment: BookingAddOnPaymentDto | null, isVendorUnderway: boolean): AddOnStatus | null => {
  /*
   * Not "nothing is happening" -- we were told nothing. The payment read sits behind a permission
   * and answers null when work-orders is unreachable, so a status here would be invented.
   */
  if (!payment) {
    return null;
  }

  /*
   * The service can ship a new reason before this package is bumped, so the lookup can miss even
   * though the type says it cannot. `?? null` is a typing guard rather than a runtime one: without
   * it the declared return widens to `AddOnStatus | null | undefined`.
   */
  const fromReason = payment.cannotPayReason ? BY_CANNOT_PAY_REASON[payment.cannotPayReason] ?? null : null;

  if (fromReason) {
    return fromReason;
  }

  if (isVendorUnderway) {
    return VENDOR_STARTED;
  }

  /* A reason with no entry in either map falls out as null, and renders no pill. */
  return (payment.cannotPayReason && WHEN_NOTHING_IS_UNDERWAY[payment.cannotPayReason]) ?? null;
};
